import type { CSSProperties } from "react";
import { ImagePlus } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/motion/ScrollReveal";
import { cn } from "@/lib/utils";
import {
  filledSponsorTiers,
  SPONSOR_TIER_IDS,
  sponsorTiers,
  stripSponsorTiers,
  type Sponsor,
  type SponsorTier,
  type SponsorTierId
} from "./data";

/*
 * Sponsor logo wall.
 *
 * Tier tone is carried by a DECORATIVE BAR ONLY — never by the heading text.
 * Measured against the white tile, the celebration accents fail AA badly
 * (accent-orange ~2.3:1, accent-teal ~2.4:1, accent ~1.9:1). PackagesGrid gets
 * away with tinted text because its tone sits on a tiny eyebrow beside a black
 * card title; here the tier name IS the content, so it stays text-foreground.
 *
 * Tailwind needs literal class names, so every tone/size string is spelled out
 * rather than templated (same convention as SponsorshipPage / PackagesGrid).
 */
const TIER_TONES: Record<SponsorTierId, { bar: string }> = {
  // The event's owners → Bursa navy, the theme's secondary and what
  // SponsorshipPage's INKIND_TONE already uses for "outside the tier palette".
  organisers: { bar: "bg-secondary" },
  // Naming right, not a rung on the paid ladder → brand red, which is what the
  // codebase already uses for "outside the tier palette".
  officialBank: { bar: "bg-primary" },
  // The four below mirror PackagesGrid's TIER_TONES exactly so the two pages agree.
  platinum: { bar: "bg-accent" },
  gold: { bar: "bg-accent-magenta" },
  silver: { bar: "bg-accent-teal" },
  bronze: { bar: "bg-accent-orange" },
  supporting: { bar: "bg-accent-violet" }
};

/*
 * PER-TIER SIZE LADDER — the share of the top rung's logo size each tier renders
 * at. This is what makes the wall state relative billing in pixels as well as in
 * words.
 *
 * The values are LINEAR multipliers on the logo's max-height, so rendered AREA
 * is the SQUARE of them. The rest of this file reasons in area, so read them
 * that way:
 *
 *     organisers / officialBank   1.00 linear   100% area   <- reference rung
 *     platinum                    0.92           85%
 *     gold                        0.83           69%
 *     silver                      0.75           56%
 *     bronze                      0.56           31%
 *     supporting                  0.45           20%
 *
 * Organisers and the Official Bank SHARE the top rung: neither sits on the paid
 * ladder (one runs the event, the other holds a category-exclusive naming
 * right), so neither outranks the other.
 *
 * ORDERING IS BY AREA ONLY — and that is a deliberate reversal, so read this
 * before "fixing" what looks like a bug.
 *
 * The wall used to guarantee that no logo in a lower tier exceeded a higher one
 * in EITHER dimension. That rule is arithmetically brutal here: equal area
 * splits between width and height by aspect ratio, so a SQUAT logo one tier down
 * still out-heights a WIDE logo one tier up unless pushed far smaller. DIALOG
 * (2.17:1, supporting) against Vistra (5.34:1, bronze) forces
 *     s_sup / s_bronze  <  sqrt(2.174 / 5.3403)  =  0.638
 * i.e. supporting <= 0.34, which is 12% of the top rung's AREA. The client read
 * that as "too small" twice, on both surfaces — so 12% area is simply not a
 * viable tier, whatever the geometry says.
 *
 * So: a lower tier now only has to be smaller in AREA. supporting sits at 0.45
 * (20% area), and the KNOWN, ACCEPTED consequence is that DIALOG renders about
 * 26% TALLER than Vistra despite being a tier below it. That is not a
 * regression, it is the trade — do not re-derive a dimension constraint to
 * "correct" it without asking the client, who chose this after seeing both.
 *
 * The silver -> bronze step (~0.75) is still steeper than the rest. That one WAS
 * derived under the old rule and is simply left as-is: it reads well and nothing
 * now forces it wider.
 *
 * Two earlier passes shipped a flatter ladder and the client read silver and
 * bronze as the same size, so DO NOT flatten this back without asking them.
 *
 * Empty tiers still get a rung, so they are right the moment they are filled.
 *
 * Unlike the ratio maths below, this is an EDITORIAL call about billing, so it
 * is hand-authored on purpose. To go back to a flat wall, set every value to 1 —
 * nothing else in this file has to change.
 */
const TIER_LOGO_SCALE: Record<SponsorTierId, number> = {
  organisers: 1.0,
  officialBank: 1.0,
  platinum: 0.92,
  gold: 0.83,
  silver: 0.75,
  bronze: 0.56,
  supporting: 0.45
};

/*
 * Show the unsold ladder tiers as dashed "slot available" tiles linking to
 * /sponsorship. OFF by default — an empty Platinum row on a credibility page
 * reads as a vacancy, not an invitation. Flip to true if the client wants the
 * open tiers advertised.
 */
const SHOW_EMPTY_TIER_SLOTS = false;

type TileSize = "standard" | "compact";

/**
 * Outer width — drives how many logos land per row at each breakpoint.
 *
 * Every tier renders at `standard`. The TILE is one size everywhere and the
 * billing hierarchy is carried by the LOGO inside it (see TIER_LOGO_SCALE);
 * shrinking the boxes too would leave the lower tiers on a ragged grid and
 * re-measure the footer strip's scroller, for no gain in clarity.
 */
export const TILE_WIDTH: Record<TileSize, string> = {
  /*
   * Widths are set to the WRAP LIMIT at each band, because logo size is bought
   * with content width and nothing else (see LOGO_FILL). Measured headroom
   * against the widest row — Organisers, 4 logos — at the container width:
   *
   *   375px   container 343   4-up wraps to 2/row   -> 160 fits (2x160 + 16 gap)
   *   768px   container 736   3/row                 -> 224 fits (3x224 + 2x24)
   *   1024px  container 992   4/row                 -> 224 only (968 of 992)
   *   1280px  container 1184  4/row                 -> 278 MAX; 272 for margin
   *
   * lg stays at 14rem: at 1024 the 4-up Organisers row already uses 968 of
   * 992px, so there is nothing to take. The xl band is where the real gain is —
   * 1280+ was leaving ~280px of width unused.
   *
   * DO NOT push xl past 17.5rem: at exactly 1280 the container is 1184px, and
   * 4 x 288 + 3 x 24 = 1224 wraps the Organisers row — which would look broken,
   * since 1024 shows it 4-up.
   */
  standard: "flex w-[10rem] sm:w-[11.5rem] md:w-[14rem] lg:w-[14rem] xl:w-[17rem]",
  /*
   * The footer strip carries its groups on every page. That row is static and
   * finite: it centres while it fits and turns into a plain horizontal scroller
   * once it doesn't, so this width trades logo size against how many sponsors
   * land on screen before anyone has to scroll.
   *
   * 11rem at md+ is set against the TRACK BUDGET, not picked round: it puts the
   * track at ~1176px inside a 1248px container at 1440, so the desktop footer
   * still does NOT scroll, with ~72px spare. 12rem would reach ~1272px and tip
   * 1440 into scrolling. The strip already scrolls at 1024 and below, which is a
   * designed state, not a fallback.
   *
   * HEADS UP: adding the second supporting logo (LFG) costs ~188px and WILL take
   * the desktop footer over 1248px. When that happens this width is the lever —
   * not the tier ladder.
   */
  compact: "flex w-[8rem] sm:w-[8.5rem] md:w-[11rem]"
};

/**
 * Inner box height + padding.
 *
 * Padding is deliberately tight: it is the ONLY thing standing between the logo
 * and a bigger render. See LOGO_FILL — max logo area at the top rung is
 * contentWidth² / rootSpanMax², so every pixel of horizontal padding costs size
 * on both surfaces. The standard tile is px-1, not px-2, because with the cards
 * gone that padding draws nothing — the gap-4/gap-6 between tiles is what
 * actually separates neighbouring logos.
 *
 * HEIGHT IS NOT A LEVER while the tile is width-bound: k = (W/H)/rootSpanMax, so
 * the H in `k x H` cancels and the logo does not change size. It only starts to
 * matter once W/H exceeds rootSpanMax (~2.23) and k clamps at 1 — past that,
 * extra width is wasted instead. xl:h-32 exists to keep the wider xl box just
 * under that clamp so its extra width still counts.
 *
 * Keep in step with TILE_GEOM below, which restates these numbers so the scale
 * can be computed. Tailwind needs literal classes, hence the duplication.
 */
const TILE_BOX: Record<TileSize, string> = {
  standard: "h-24 px-1 py-1 md:h-28 xl:h-32",
  /*
   * The compact tile is HEIGHT-bound, the opposite of the wall: its W/H (2.11)
   * exceeds the strip's rootSpanMax (1.875, set by UOB), so k clamps at 1 and
   * WIDENING IT ALONE DOES NOTHING — measured +0%. Height is what moves the
   * logos here, up to H = W/rootSpan, past which the extra height is wasted
   * instead. Hence both grew together: 160x80 -> 176x96.
   */
  compact: "h-20 px-1 py-1 md:h-24"
};

/**
 * Border width TILE_BASE draws, in px. **0 since the cards were removed.**
 *
 * Kept as a named constant rather than deleted, because scaleFor() has to
 * subtract whatever the tile actually draws: box-sizing is border-box, so a
 * border eats into the content box and every k is derived from that box. If a
 * border ever comes back, change this WITH it — otherwise every logo is sized
 * against a content box 2px larger than reality and the whole set drifts.
 */
const TILE_BORDER = 0;

type Band = "base" | "sm" | "md" | "lg" | "xl";

/**
 * The numeric form of TILE_WIDTH + TILE_BOX, in px. One fact in two forms — the
 * classes are what render, this is what the scale is derived from, so THEY MUST
 * BE UPDATED TOGETHER. (Tailwind can't consume computed values and JS can't read
 * a class, so neither can be generated from the other.)
 */
const TILE_GEOM: Record<TileSize, Record<Band, { w: number; h: number; px: number; py: number }>> = {
  standard: {
    base: { w: 160, h: 96, px: 4, py: 4 }, //      w-[10rem] h-24 px-1 py-1
    sm: { w: 184, h: 96, px: 4, py: 4 }, //     sm:w-[11.5rem]
    md: { w: 224, h: 112, px: 4, py: 4 }, //     md:w-[14rem] md:h-28
    lg: { w: 224, h: 112, px: 4, py: 4 }, //     lg:w-[14rem] (1024 is full at 4-up)
    xl: { w: 272, h: 128, px: 4, py: 4 } //     xl:w-[17rem] xl:h-32
  },
  compact: {
    base: { w: 128, h: 80, px: 4, py: 4 }, //       w-[8rem] h-20 px-1 py-1
    sm: { w: 136, h: 80, px: 4, py: 4 }, //     sm:w-[8.5rem]
    md: { w: 176, h: 96, px: 4, py: 4 }, //     md:w-[11rem] md:h-24
    lg: { w: 176, h: 96, px: 4, py: 4 }, //     (no lg override — md carries on)
    xl: { w: 176, h: 96, px: 4, py: 4 } //     (no xl override either — track budget)
  }
};

/*
 * EQUAL OPTICAL AREA WITHIN A TIER — why the logos in one row are not all the
 * same height.
 *
 * A 5.3:1 wordmark (Vistra) and a 2.1:1 badge (DCD) cannot share a height and
 * look equal; the wordmark would tower over everything. What reads as "the same
 * size" is equal AREA, so that is what this computes — and then TIER_LOGO_SCALE
 * steps that area down, deliberately, from one tier to the next.
 *
 * Give the logo a max-height of `k / sqrt(ratio) * s` as a PERCENTAGE of the
 * tile's content height H, where s is the tier's scale. Rendered area is then
 *     (p*H)^2 * ratio  =  (k/sqrt(r) * s * H)^2 * r  =  k^2 * s^2 * H^2
 * — independent of ratio, so every logo in a tier renders at the same area, and
 * each tier renders at s^2 of the top rung. Because p is a percentage, both
 * hold at every breakpoint with no per-breakpoint px math.
 *
 * k is capped so the WIDEST RENDERED mark still fits the width. Per logo the
 * constraint is `k * sqrt(r) * s * H <= W`, so what binds is the largest
 * `sqrt(ratio) * tierScale` on the surface, not the largest raw ratio —
 * see maxRootSpan. Push k past that and the widest marks clamp on max-w-full
 * instead, which breaks the equality and the tier goes visibly uneven.
 *
 * NOTHING IN THIS DERIVATION IS HAND-TUNED. The spans come from the sponsor data
 * and the tier ladder, the caps come from TILE_GEOM, so adding a sponsor of ANY
 * aspect ratio to ANY tier re-derives every k, at every breakpoint, on both
 * surfaces, automatically. That is deliberate: an earlier version hardcoded
 * numbers against Vistra being the widest logo at 5.34:1, which would have
 * silently broken on the next wider sponsor. Adding a logo stays a
 * one-object-literal edit in data.ts.
 *
 * k is per breakpoint BAND because TILE_WIDTH and TILE_BOX change at different
 * breakpoints, so the tile's proportions (and therefore the cap) differ. Note md
 * is TIGHTER than sm for the standard tile — it grows taller but barely wider —
 * which is why sm and md need separate bands rather than sharing one.
 *
 * THIS ONLY HOLDS IF EVERY FILE IS CROPPED TO ITS INK BOUNDS. The ratio is read
 * from the file, so baked-in whitespace is indistinguishable from the mark and
 * silently shrinks it. See the crop note at the top of data.ts.
 */

/**
 * The widest mark AS RENDERED in a set of tiers, as `sqrt(ratio) * tierScale`.
 * 1 if the set is empty.
 *
 * The tier scale belongs in here because the cap is a statement about what is
 * actually on screen. Leaving it out would still be SAFE — the cap would merely
 * be too tight — but it would hold every tier back on account of a logo that is
 * no longer the widest thing rendered. That is not hypothetical: the widest file
 * on the wall is Vistra at 5.34:1, and it sits in BRONZE, the smallest rung.
 * Including the scale hands roughly 4% back on the wall and 8% on the strip.
 */
function maxRootSpan(tiers: SponsorTier[]): number {
  const spans = tiers.flatMap((t) =>
    t.sponsors.map((s) => Math.sqrt(s.width / s.height) * TIER_LOGO_SCALE[t.id])
  );
  const max = Math.max(...spans);
  return Number.isFinite(max) && max > 0 ? max : 1;
}

/**
 * Widest logo actually rendered on each surface. Scoped per surface, not global:
 * the strip omits the organisers (see stripSponsorTiers), so a very wide
 * organiser logo must not shrink the footer.
 */
const ROOT_SPAN_MAX: Record<TileSize, number> = {
  standard: maxRootSpan(filledSponsorTiers),
  compact: maxRootSpan(stripSponsorTiers)
};

/**
 * How much of the geometric maximum to use. THE tuning knob — the one number to
 * change if the logos should be bigger or smaller overall.
 *   1.00 = the widest RENDERED logo exactly spans the content box edge to edge
 *   0.99 = a hair of slack, so subpixel rounding can never trip a width clamp
 *
 * A clamp is not cosmetic: it is what breaks equal area, so if a logo ever
 * renders width-clamped, LOWER THIS (0.98, then 0.97) rather than living with an
 * uneven tier. Verify by measuring rendered area in-browser — within any single
 * tier the spread must be 1.00x.
 *
 * (This used to also carry a proof that logos stayed clear of the ExternalLink
 * icon. That icon went with the cards, so the constraint is gone — the only
 * thing LOGO_FILL now guards is the width clamp.)
 */
const LOGO_FILL = 0.99;

/** Equal-area scale for one tile size at one breakpoint band, before the tier ladder. */
function scaleFor(size: TileSize, band: Band): number {
  const g = TILE_GEOM[size][band];
  const w = g.w - 2 * g.px - 2 * TILE_BORDER;
  const h = g.h - 2 * g.py - 2 * TILE_BORDER;
  // min(1, …) so a very short/wide tile can't push the logo past its own box height.
  return Math.min(1, w / h / ROOT_SPAN_MAX[size]) * LOGO_FILL;
}

/*
 * The widest rendered mark sets the pace for everyone on its surface. That is
 * correct — it is what keeps each tier even — but it means one much wider logo
 * legitimately shrinks the whole surface, which is surprising if you only added
 * one sponsor. Say so in dev rather than let someone hunt for it.
 */
if (import.meta.env.DEV) {
  // The old raw-ratio threshold, recast into root-span terms so it still means
  // "a 5.5:1 mark at the TOP rung". The same mark lower down the ladder renders
  // smaller and no longer binds — which is the whole point of the ladder.
  const DESIGN_RATIO = 5.5;
  const DESIGN_ROOT_SPAN = Math.sqrt(DESIGN_RATIO);
  for (const [size, span] of Object.entries(ROOT_SPAN_MAX)) {
    if (span > DESIGN_ROOT_SPAN) {
      console.warn(
        `[SponsorWall] The widest ${size} mark now renders as wide as a ${(span ** 2).toFixed(2)}:1 ` +
          `logo at the top rung (designed around ${DESIGN_RATIO.toFixed(2)}:1), so every logo on ` +
          `that surface is rendering smaller to stay even with it. Options: re-crop or use a ` +
          `stacked lockup for that sponsor, give it an opticalScale in data.ts, or accept the ` +
          `smaller set.`
      );
    }
  }

  /*
   * TIER ORDER GUARD — is the billing ladder itself well-formed?
   *
   * This USED to assert that no logo in a lower tier exceeded a higher one in
   * either dimension. That check is gone on purpose: ordering is now by AREA
   * only (see TIER_LOGO_SCALE), and a squat logo in a lower tier legitimately
   * renders TALLER than a wide one above it — DIALOG vs Vistra does exactly
   * that, by ~26%, and it is the accepted trade. Keeping the old check would
   * have fired on every page load, and a warning that is always on is a warning
   * nobody reads.
   *
   * What is left to verify is weaker but not worthless. Rendered area is
   * k^2 * s^2 * H^2, and k and H are common to a surface, so area ordering
   * reduces exactly to "TIER_LOGO_SCALE descends with billing rank". That is
   * true by construction — until someone fat-fingers a rung or inserts a tier in
   * the wrong place in SPONSOR_TIER_IDS, which is precisely the mistake worth
   * catching cheaply.
   *
   * Equal rungs are allowed (organisers and officialBank deliberately tie at
   * 1.00); only a rung that is BIGGER than the one above it is a fault.
   */
  {
    const rungs = SPONSOR_TIER_IDS.map((id) => ({ id, scale: TIER_LOGO_SCALE[id] }));
    for (let i = 1; i < rungs.length; i++) {
      const above = rungs[i - 1];
      const here = rungs[i];
      if (here.scale > above.scale) {
        console.warn(
          `[SponsorWall] LADDER OUT OF ORDER: "${here.id}" is set to ${here.scale}, LARGER than ` +
            `"${above.id}" (${above.scale}) which outranks it — so a lower tier would render ` +
            `bigger in area than a higher one. Fix the values in TIER_LOGO_SCALE, or the tier ` +
            `order in SPONSOR_TIER_IDS (data.ts) if the ranking itself changed.`
        );
      }
    }
  }
}

/*
 * Emitted as CSS custom properties rather than one maxHeight, because the value
 * has to change at breakpoints and inline styles can't hold media queries. The
 * consuming classes (max-h-[var(--logo-mh)] etc.) are static strings, so
 * Tailwind still generates them — an interpolated arbitrary value would not.
 */
function logoScaleVars(sponsor: Sponsor, size: TileSize, tier: SponsorTierId): CSSProperties {
  const ratio = sponsor.width / sponsor.height;
  // The tier ladder and the per-sponsor nudge MULTIPLY, so a sponsor's nudge
  // stays relative to its own tier rather than to the top rung.
  const scale = TIER_LOGO_SCALE[tier] * (sponsor.opticalScale ?? 1);
  const pct = (band: Band) =>
    `${((scaleFor(size, band) / Math.sqrt(ratio)) * scale * 100).toFixed(2)}%`;
  return {
    "--logo-mh": pct("base"),
    "--logo-mh-sm": pct("sm"),
    "--logo-mh-md": pct("md"),
    "--logo-mh-lg": pct("lg"),
    "--logo-mh-xl": pct("xl")
  } as CSSProperties;
}

/*
 * NO CARD — the logo IS the element. The tile survives only as an invisible
 * layout box and, deliberately, as the HIT TARGET: at their tier scales several
 * marks are tiny (DCD renders ~19px tall inside a 94px box), so shrinking the
 * target down to the ink would hurt touch users and WCAG 2.5.8. Hovering
 * anywhere in the box is intended, not a bug.
 *
 * What replaces the card as the "this is clickable" signal:
 *   - cursor-pointer, set EXPLICITLY rather than inherited from the UA's <a>
 *     default. It already resolved to pointer, but with no card this is now the
 *     primary affordance and shouldn't rest on a browser default.
 *   - the hover lift here, plus a scale on the <img> in SponsorTile — a cursor
 *     does nothing for touch users, so there has to be a visible change too.
 *   - focus-visible ring for keyboard users.
 *
 * ring-offset-card, NOT -background: the surface behind these is now white (see
 * the section in SponsorWall), so a background-coloured offset would draw a
 * pale halo inside the ring.
 *
 * The white-matte constraint that used to force bg-card here has moved UP to
 * that section — see its comment before changing either.
 */
/*
 * THE WALL SITS ON PURE WHITE, AND THAT IS LOAD-BEARING — not a style choice.
 *
 * 5 of the 9 logo files are a hard WHITE MATTE rather than transparency (Mah
 * Sing, Vistra, UOB, The Edge Malaysia, Yayasan Bursa; only Bursa, TEEF, SD
 * Guthrie and DCD have real alpha). While each logo sat on its own white card
 * that was invisible. With the cards gone, anything but a white surface behind
 * them paints five white rectangles across the wall — on the page's pale-sky
 * #F4F8FC they are plainly visible.
 *
 * So: DO NOT tint this section, and audit any NEW logo's alpha before adding it
 * (`modal opaque colour == white` means matted). The alternative is re-keying
 * artwork the sponsors supplied, which haloes on anti-aliased edges — The Edge
 * Malaysia has teal edge pixels that fringe.
 *
 * bg-card (not bg-white) so it still follows the theme token, and full-bleed —
 * painting it on the `container` instead would leave pale margins at wide
 * viewports, i.e. one giant card, which is the opposite of the intent.
 */
const WALL_SURFACE = "bg-card";

const TILE_BASE =
  "group relative flex w-full cursor-pointer items-center justify-center rounded-lg " +
  "transition-transform duration-200 motion-safe:hover:-translate-y-0.5 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-card";

export function SponsorTile({
  sponsor,
  tier,
  size = "standard",
  eager = false
}: {
  sponsor: Sponsor;
  /**
   * The tier this logo is billed under — required, not defaulted, because a
   * silent fallback to the top rung would put a bronze logo at organiser size
   * and nothing would fail. Both call sites (the wall and the footer strip)
   * already have it to hand.
   */
  tier: SponsorTierId;
  size?: TileSize;
  eager?: boolean;
}) {
  const { t } = useTranslation("sponsors");

  const logo = (
    <img
      src={sponsor.logo}
      alt={sponsor.name}
      width={sponsor.width}
      height={sponsor.height}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      /*
       * Inline, not aspect-[w/h] / max-h-[x%] classes: both values are computed
       * from data and Tailwind arbitrary values can't be templated.
       *
       * aspectRatio is required, not cosmetic — Header.tsx documents the Safari
       * flexbox <img> bug where a wide image renders at near-natural size and
       * blows up the layout, and these tiles are flex containers with wide
       * <img> children. maxHeight is what equalises optical area within a tier
       * and steps it down between tiers (see LOGO_FILL and TIER_LOGO_SCALE);
       * max-w-full below is the backstop that clamps the widest marks on narrow
       * screens.
       */
      style={{
        aspectRatio: `${sponsor.width} / ${sponsor.height}`,
        ...logoScaleVars(sponsor, size, tier)
      }}
      /* The scale is the touch/visual half of the affordance — cursor-pointer on
         the link covers mice only. transform-only, so it can't reflow the row. */
      className="w-auto max-w-full object-contain transition-transform duration-200 motion-safe:group-hover:scale-[1.06] max-h-[var(--logo-mh)] sm:max-h-[var(--logo-mh-sm)] md:max-h-[var(--logo-mh-md)] lg:max-h-[var(--logo-mh-lg)] xl:max-h-[var(--logo-mh-xl)]"
    />
  );

  return (
    <a
      href={sponsor.href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(TILE_BASE, TILE_BOX[size])}
    >
      {logo}
      {/* The alt text is the link's accessible name; this appends the new-tab
          warning to it (WCAG G201). No aria-label on the <a> — it would
          override the alt and leave the image unnamed. */}
      <span className="sr-only"> {t("logo.opensInNewTab")}</span>
      {/* No visible external-link icon since the cards were removed: it was
          anchored to the card's bottom-right corner, and with no card it floats
          in empty space — for a short mark like DCD, ~35px below the logo. The
          sr-only text above is what actually satisfies WCAG G201; the icon was
          the sighted-user nicety. */}
    </a>
  );
}

function TierHeading({ tier }: { tier: SponsorTier }) {
  const { t } = useTranslation("sponsors");
  return (
    <ScrollReveal className="flex flex-col items-center text-center">
      <span aria-hidden className={cn("h-1 w-12 rounded-full", TIER_TONES[tier.id].bar)} />
      <h2 className="mt-4 text-2xl md:text-3xl">{t(`tiers.${tier.id}`)}</h2>
    </ScrollReveal>
  );
}

/*
 * NOTE: still styled as a dashed CARD, which no longer matches the wall now
 * that the sponsor tiles have none. It is unreachable (SHOW_EMPTY_TIER_SLOTS is
 * false), so it is left as-is rather than redesigned speculatively — but it
 * needs restyling before that flag is ever flipped on.
 */
function EmptySlot() {
  const { t } = useTranslation("sponsors");
  return (
    <Link
      to="/sponsorship/"
      className="grid h-24 w-full place-items-center rounded-xl border-2 border-dashed border-border bg-muted/40 px-5 py-4 text-center transition-colors duration-200 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:h-28"
    >
      <span>
        <ImagePlus aria-hidden className="mx-auto h-6 w-6 text-foreground/40" />
        <span className="mt-2 block text-sm text-muted-foreground">{t("emptySlot.title")}</span>
      </span>
    </Link>
  );
}

export function SponsorWall() {
  const { t } = useTranslation("sponsors");
  const tiers = SHOW_EMPTY_TIER_SLOTS ? sponsorTiers : filledSponsorTiers;

  // Fresh year, nothing announced yet — say so rather than render a hero, a
  // hole, then a CTA.
  if (tiers.length === 0) {
    return (
      <section className={WALL_SURFACE}>
        <div className="container py-16 md:py-20">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="text-2xl md:text-3xl">{t("pending.title")}</h2>
            <p className="mt-3 text-muted-foreground">{t("pending.body")}</p>
          </ScrollReveal>
        </div>
      </section>
    );
  }

  return (
    <section className={WALL_SURFACE}>
      <div className="container py-16 md:py-20">
        {tiers.map((tier, index) => {
          const tierLabel = t(`tiers.${tier.id}`);

          return (
            <div
              key={tier.id}
              /* A rule between tiers, mirroring the reference wall. Not the
                 alternating bg-muted/30 band idiom — with up to six tiers that
                 zebra-stripes the whole page. */
              className={
                index === 0 ? undefined : "mt-14 border-t border-border pt-14 md:mt-16 md:pt-16"
              }
            >
              <TierHeading tier={tier} />

              {/* Centred flex-wrap rather than a grid: a tier may hold one logo
                  or eight, and a grid leaves a lone logo hard-left and a partial
                  row with a hole. Wrapping centres every row at any count. */}
              <ScrollReveal.Group
                role="list"
                aria-label={tierLabel}
                className="mt-8 flex flex-wrap items-stretch justify-center gap-4 md:gap-6"
                stagger={0.07}
              >
                {tier.sponsors.map((sponsor) => (
                  <ScrollReveal.Item
                    key={sponsor.id}
                    role="listitem"
                    className={TILE_WIDTH.standard}
                  >
                    <SponsorTile sponsor={sponsor} tier={tier.id} eager={index === 0} />
                  </ScrollReveal.Item>
                ))}

                {SHOW_EMPTY_TIER_SLOTS && tier.sponsors.length === 0 && (
                  <ScrollReveal.Item role="listitem" className={TILE_WIDTH.standard}>
                    <EmptySlot />
                  </ScrollReveal.Item>
                )}
              </ScrollReveal.Group>
            </div>
          );
        })}
      </div>
    </section>
  );
}
